// File: src/components/Admin/AddOnServices.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Wifi as WifiIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import activityLog from '../../services/activityLog.service';
import InternetAvailabilityChecker from './InternetAvailabilityChecker';

interface AddOnService {
  id: number;
  name: string;
  description: string;
  category: 'internet' | 'storage' | 'security' | 'cloud' | 'email' | 'hardware' | 'other';
  chargeType: 'recurring' | 'one_time';
  price: number;
  setupFee: number;
  billingCycle: 'monthly' | 'quarterly' | 'annually' | 'one_time';
  isActive: boolean;
  features: string[];
  metadata?: any;
}

const categoryIcons = {
  internet: <WifiIcon />,
  storage: <StorageIcon />,
  security: <SecurityIcon />,
  cloud: <CloudIcon />,
  email: <EmailIcon />,
  hardware: <ComputerIcon />,
  other: <PhoneIcon />,
};

const AddOnServices: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { member } = useAuth();
  const [services, setServices] = useState<AddOnService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<AddOnService | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<AddOnService | null>(null);
  const [availabilityCheckerOpen, setAvailabilityCheckerOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    category: string;
    chargeType: string;
    price: string | number;
    setupFee: string | number;
    billingCycle: string;
    isActive: boolean;
    features: string;
  }>({
    name: '',
    description: '',
    category: 'other',
    chargeType: 'recurring',
    price: '',
    setupFee: '',
    billingCycle: 'monthly',
    isActive: true,
    features: '',
  });

  useEffect(() => {
    if (member?.memberType === 'master_admin') {
      fetchServices();
    }
  }, [member]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/addonservices');
      if (response.data.success && response.data.data?.services) {
        setServices(response.data.data.services);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load add-on services');
      activityLog.error('fetch_addon_services', 'Failed to load add-on services', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (service?: AddOnService) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name,
        description: service.description,
        category: service.category,
        chargeType: service.chargeType,
        price: service.price,
        setupFee: service.setupFee,
        billingCycle: service.billingCycle,
        isActive: service.isActive,
        features: service.features.join('\n'),
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        description: '',
        category: 'other',
        chargeType: 'recurring',
        price: '',
        setupFee: '',
        billingCycle: 'monthly',
        isActive: true,
        features: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingService(null);
  };

  const handleSubmit = async () => {
    try {
      const endpoint = editingService 
        ? `/endpoints/addonservices/${editingService.id}`
        : '/endpoints/addonservices';
      const method = editingService ? 'put' : 'post';
      
      // Convert features string to array
      const featuresArray = formData.features
        .split('\n')
        .map(f => f.trim())
        .filter(f => f.length > 0);
      
      const submitData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        chargeType: formData.chargeType,
        price: formData.price === '' ? 0 : Number(formData.price),
        setupFee: formData.setupFee === '' ? 0 : Number(formData.setupFee),
        billingCycle: formData.billingCycle,
        isActive: formData.isActive,
        features: featuresArray,
      };
      
      const response = await api[method](endpoint, submitData);
      
      if (response.data.success) {
        const successMessage = editingService ? 'Add-on service updated successfully' : 'Add-on service created successfully';
        activityLog.success(
          editingService ? 'update_addon_service' : 'add_addon_service',
          `${successMessage}: ${formData.name}`,
          { serviceId: editingService?.id || response.data.service_id }
        );
        setSuccess(successMessage);
        handleCloseDialog();
        fetchServices();
      }
    } catch (err: any) {
      activityLog.error('addon_service_submit', 'Failed to save add-on service', err);
      setError(err.response?.data?.message || 'Failed to save add-on service');
    }
  };

  const handleDeleteClick = (service: AddOnService) => {
    setServiceToDelete(service);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!serviceToDelete) return;

    try {
      const response = await api.delete(`/endpoints/addonservices/${serviceToDelete.id}`);
      
      if (response.data.success) {
        activityLog.success('delete_addon_service', `Deleted add-on service: ${serviceToDelete.name}`, {
          serviceId: serviceToDelete.id,
          serviceName: serviceToDelete.name,
        });
        setSuccess('Add-on service deleted successfully');
        setDeleteDialogOpen(false);
        setServiceToDelete(null);
        fetchServices();
      }
    } catch (err: any) {
      activityLog.error('delete_addon_service', 'Failed to delete add-on service', err);
      setError(err.response?.data?.message || 'Failed to delete add-on service');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleServiceSelected = (serviceId: number) => {
    // Find the service and open edit dialog
    const service = services.find(s => s.id === serviceId);
    if (service) {
      handleOpenDialog(service);
    }
  };

  // Common props for number inputs to hide spinners
  const numberInputProps = {
    style: { MozAppearance: 'textfield' as const },
    sx: {
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
    },
  };

  if (member?.memberType !== 'master_admin') {
    return (
      <Alert severity="error">
        You do not have permission to access this section.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Add-On Services</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<WifiIcon />}
            onClick={() => setAvailabilityCheckerOpen(true)}
          >
            Check Internet Availability
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create New Service
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {isMobile ? (
        // Mobile Card View
        <Box>
          {services.map((service) => (
            <Card key={service.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start">
                  <Box display="flex" alignItems="center" gap={1}>
                    {categoryIcons[service.category]}
                    <Box>
                      <Typography variant="h6">{service.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {service.description}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={service.isActive ? 'Active' : 'Inactive'}
                    color={service.isActive ? 'success' : 'error'}
                    size="small"
                  />
                </Box>

                <Box mt={2}>
                  <Typography variant="h5" color="primary">
                    ${service.price.toFixed(2)}
                    {service.chargeType === 'recurring' && `/${service.billingCycle}`}
                  </Typography>
                  {service.chargeType === 'one_time' && (
                    <Chip label="One-time" size="small" color="info" sx={{ ml: 1 }} />
                  )}
                  {service.setupFee > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      + ${service.setupFee.toFixed(2)} setup
                    </Typography>
                  )}
                </Box>

                {service.features.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>Features:</Typography>
                    <List dense>
                      {service.features.map((feature, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </CardContent>
              <CardActions>
                <IconButton
                  color="primary"
                  onClick={() => handleOpenDialog(service)}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => handleDeleteClick(service)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Service Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Pricing</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell>Features</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.map((service) => (
                <TableRow key={service.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {categoryIcons[service.category]}
                      {service.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={service.category} size="small" />
                  </TableCell>
                  <TableCell>{service.description}</TableCell>
                  <TableCell>
                    <Chip 
                      label={service.chargeType === 'recurring' ? 'Recurring' : 'One-time'} 
                      size="small"
                      color={service.chargeType === 'recurring' ? 'primary' : 'info'}
                    />
                  </TableCell>
                  <TableCell>
                    ${service.price.toFixed(2)}
                    {service.setupFee > 0 && (
                      <Chip
                        label={`+$${service.setupFee.toFixed(2)} setup`}
                        size="small"
                        color="warning"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {service.chargeType === 'recurring' ? service.billingCycle : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <List dense disablePadding>
                      {service.features.slice(0, 3).map((feature, index) => (
                        <ListItem key={index} sx={{ py: 0 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature}
                            primaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                      {service.features.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{service.features.length - 3} more
                        </Typography>
                      )}
                    </List>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={service.isActive ? 'Active' : 'Inactive'}
                      color={service.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(service)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(service)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Service Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingService ? 'Edit Add-On Service' : 'Create New Add-On Service'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Service Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="internet">Internet</MenuItem>
                  <MenuItem value="storage">Storage</MenuItem>
                  <MenuItem value="security">Security</MenuItem>
                  <MenuItem value="cloud">Cloud Services</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="hardware">Hardware</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Charge Type</InputLabel>
                <Select
                  value={formData.chargeType}
                  onChange={(e) => {
                    handleInputChange('chargeType', e.target.value);
                    // Auto-set billing cycle for one-time charges
                    if (e.target.value === 'one_time') {
                      handleInputChange('billingCycle', 'one_time');
                    } else if (formData.billingCycle === 'one_time') {
                      handleInputChange('billingCycle', 'monthly');
                    }
                  }}
                  label="Charge Type"
                >
                  <MenuItem value="recurring">Recurring</MenuItem>
                  <MenuItem value="one_time">One-time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={formData.chargeType === 'recurring' ? 'Recurring Price' : 'Price'}
                type="number"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={numberInputProps}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Setup Fee"
                type="number"
                value={formData.setupFee}
                onChange={(e) => handleInputChange('setupFee', e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={numberInputProps}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={formData.chargeType === 'one_time'}>
                <InputLabel>Billing Cycle</InputLabel>
                <Select
                  value={formData.billingCycle}
                  onChange={(e) => handleInputChange('billingCycle', e.target.value)}
                  label="Billing Cycle"
                >
                  {formData.chargeType === 'recurring' ? (
                    <>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="quarterly">Quarterly</MenuItem>
                      <MenuItem value="annually">Annually</MenuItem>
                    </>
                  ) : (
                    <MenuItem value="one_time">One-time</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features (one per line)"
                multiline
                rows={5}
                value={formData.features}
                onChange={(e) => handleInputChange('features', e.target.value)}
                helperText="Enter each feature on a new line"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingService ? 'Update Service' : 'Create Service'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the add-on service "{serviceToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Internet Availability Checker */}
      <InternetAvailabilityChecker
        open={availabilityCheckerOpen}
        onClose={() => setAvailabilityCheckerOpen(false)}
        onSelectService={handleServiceSelected}
      />
    </Box>
  );
};

export default AddOnServices;